import { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface FilterSectionProps {
  title: string;
  excludedCount?: number;
  /** Number of securities explicitly included (allowed list). Shown as a green badge. */
  includedCount?: number;
  /** Optional badge rendered next to the title (e.g. "Coming Soon"). */
  badge?: React.ReactNode;
  /** Initial state for uncontrolled mode. Ignored when `open` is provided. */
  defaultOpen?: boolean;
  /** Controlled open state. When provided, the component is fully controlled. */
  open?: boolean;
  /** Called when the user toggles the section. Required for controlled mode. */
  onToggle?: () => void;
  /** When false, the section is a static header — no chevron, not clickable,
   *  and no body. Used for "Coming Soon" surfaces that have nothing to expand
   *  into yet. Defaults to true (collapsible). */
  collapsible?: boolean;
  children?: React.ReactNode;
}

export function FilterSection({
  title,
  excludedCount = 0,
  includedCount = 0,
  badge,
  defaultOpen,
  open: controlledOpen,
  onToggle,
  collapsible = true,
  children,
}: FilterSectionProps) {
  // Uncontrolled fallback: used only when `open` prop is not provided
  const [internalOpen, setInternalOpen] = useState(defaultOpen ?? true);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  const handleToggle = () => {
    if (isControlled) {
      onToggle?.();
    } else {
      setInternalOpen((prev) => !prev);
    }
  };

  // Title + badges — shared by the collapsible trigger and the static header
  // so both variants read identically (same padding lives on the wrapper).
  const heading = (
    <div className="flex items-center gap-2">
      <span className="font-medium text-[15px]">{title}</span>
      {badge}
      {excludedCount > 0 && (
        <span className="text-xs px-2 py-0.5 rounded-md border border-destructive/20 bg-destructive/5 text-destructive/80 font-medium">
          {excludedCount.toLocaleString()} excluded
        </span>
      )}
      {includedCount > 0 && (
        <span className="text-xs px-2 py-0.5 rounded-md border border-green-500/20 bg-green-500/5 text-green-700 font-medium">
          {includedCount.toLocaleString()} included
        </span>
      )}
    </div>
  );

  // Wrapper card + trigger — verbatim from figma UniverseSelection.tsx
  // (via MCP). Background is bg-muted/30 (not bg-card), border is
  // border border-border/75, and the chevron is ChevronRight that
  // rotates 90deg clockwise when the section is open (pointing down)
  // instead of ChevronDown rotating 180.
  return (
    <div className="rounded-lg border border-border/75 bg-muted/30 overflow-hidden">
      {collapsible ? (
        <>
          <button
            type="button"
            onClick={handleToggle}
            className="flex items-center justify-between w-full px-3 py-2.5 text-left cursor-pointer hover:opacity-80 transition-opacity"
          >
            {heading}
            <ChevronRight
              className={cn(
                'w-4 h-4 text-muted-foreground/50 transition-transform duration-200',
                isOpen && 'rotate-90',
              )}
            />
          </button>
          {isOpen && (
            <div className="px-3 pb-3 pt-1.5 space-y-5">{children}</div>
          )}
        </>
      ) : (
        // Static header — no chevron, not clickable, no body.
        <div className="flex items-center justify-between w-full px-3 py-2.5">
          {heading}
        </div>
      )}
    </div>
  );
}
